/**
 * The producibility TABLE (R15) — what each occurrence needs from a pack
 * before the pack can ever emit it, answered at a named tier.
 *
 * @remarks
 * Split from `occurrence-producibility.ts` at the 500-line cap (D152 added
 * the spent refusal's rule): this file is the requirements, that file is the
 * judge that walks a pack's listeners against them. The rules' provenance and
 * the honest limits of the judgment are stated there.
 *
 * @packageDocumentation
 */

import { type ContentPackDefinition, refusedActsOf } from '@orpheus/content-schema';
import { ENGINE_CLAIMED_INTENT_TOKENS, INTENTS_PRODUCING_ACTION, NARROWABLE_INTENT_TOKENS } from '@orpheus/engine-runtime';
import { type CompiledLexicon } from '@orpheus/parser-engine';

/** One requirement an occurrence needs from the pack, answered at a named tier. */
export interface ProducibilityRule {
	/**
	 * Whether the pack satisfies this requirement; `undefined` means "cannot
	 * judge this load" (a lexicon-tier rule with no compiled lexicon) and
	 * abstains — the table never guesses.
	 */
	readonly holds: (contentPack: ContentPackDefinition, lexicon: CompiledLexicon | undefined) => boolean | undefined;
	/** Author-facing statement of what the pack lacks when the rule fails. */
	readonly requirement: string;
	readonly tier: 'lexicon' | 'static';
}

/** Build a static-tier rule (a structural fact of the parsed pack). */
const staticRule = (
	requirement: string,
	holds: (contentPack: ContentPackDefinition) => boolean,
): ProducibilityRule => ({ holds: (contentPack) => holds(contentPack), requirement, tier: 'static' });

/** Build a lexicon-tier rule (a fact of the merged vocabulary; abstains without one). */
const lexiconRule = (
	requirement: string,
	holds: (lexicon: CompiledLexicon) => boolean,
): ProducibilityRule => ({
	holds: (_contentPack, lexicon) => (lexicon === undefined ? undefined : holds(lexicon)),
	requirement,
	tier: 'lexicon',
});

/**
 * Is any of these canonical tokens a SUPPORTED verb in the merged vocabulary?
 * The narrowing roster refuses declared-unsupported verbs by name (D35), so a
 * roster-driven occurrence needs a verb the roster will actually run.
 */
const hasSupportedVerb = (lexicon: CompiledLexicon, tokens: readonly string[]): boolean =>
	tokens.some((token) => lexicon.intentsByToken.get(token)?.isUnsupported === false);

/**
 * Is any of these canonical tokens PRESENT at all (supported or not)? The
 * interceptors run BEFORE the D35 unsupported refusal, so `turn` — declared
 * unsupported by design (D56) — still mints the switch actions, and presence
 * is the honest bar for interceptor-driven occurrences.
 */
const hasVerb = (lexicon: CompiledLexicon, tokens: readonly string[]): boolean =>
	tokens.some((token) => lexicon.intentsByToken.has(token));

/** Can the player TYPE a movement verb ("go north"), as opposed to a bare direction? */
const hasTypedMovementVerb = (lexicon: CompiledLexicon): boolean =>
	[...lexicon.intentsByToken.values()].includes(lexicon.movementIntent);

/** Every exit in the pack, wherever its room declares it. */
const allExits = (contentPack: ContentPackDefinition): readonly ContentPackDefinition['rooms'][number]['exits'][number][] =>
	contentPack.rooms.flatMap((room) => room.exits);

/** Every authored use interaction in the pack. */
const allUseInteractions = (
	contentPack: ContentPackDefinition,
): readonly ContentPackDefinition['items'][number]['useInteractions'][number][] =>
	contentPack.items.flatMap((item) => item.useInteractions);

/** Is this item takeable — portable and not ambient (D19b)? */
const isTakeable = (contentPack: ContentPackDefinition, item: ContentPackDefinition['items'][number]): boolean =>
	item.isPortable && !contentPack.ambientItemIds.includes(item.itemId);

/** The one requirement several door- and search-shaped reasons share. */
const requiresAnOpenable = staticRule('at least one item declaring `openable` (a door to be shut)', (contentPack) =>
	contentPack.items.some((item) => item.openable !== undefined),
);

/** The requirement the take-shaped reasons share. */
const requiresATakeable = staticRule('at least one portable, non-ambient item', (contentPack) =>
	contentPack.items.some((item) => isTakeable(contentPack, item)),
);

/** D143: the requirement the wear-shaped occurrences share. */
const requiresAWearable = staticRule('at least one item declaring `equippable`', (contentPack) =>
	contentPack.items.some((item) => item.equippable !== undefined),
);

/** D143: a supported wear verb, either direction. */
const requiresAWearVerb = lexiconRule('a supported `equip`, `unequip` or `remove` verb in the merged vocabulary', (lexicon) =>
	hasSupportedVerb(lexicon, ['equip', 'remove', 'unequip']),
);

/** D234: a foe — an actor declaring `combat` — the requirement the strike-shaped occurrences share. */
/** D244: a stock to draw from — and the verb that draws. */
const requiresAStock = staticRule('at least one item declaring a `stock`', (contentPack) => contentPack.items.some((item) => item.stock !== undefined));

const requiresATakeVerb = lexiconRule('a supported `take` or `remove` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['remove', 'take']));

const requiresAFoe = staticRule('at least one actor declaring `combat`', (contentPack) => contentPack.actors.some((actor) => actor.combat !== undefined));

/** D234: a supported `attack` verb. */
const requiresAnAttackVerb = lexiconRule('a supported `attack` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['attack']));

/** D235: a supported gift verb — `give` or `show`. */
const requiresAGiftVerb = lexiconRule('a supported `give` or `show` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['give', 'show']));

/** D252: somebody who can be told something — an actor declaring `orders`. */
const requiresAnOrderTaker = staticRule('at least one actor declaring `orders`', (contentPack) =>
	contentPack.actors.some((actor) => Object.keys(actor.orders ?? {}).length > 0),
);

/** D235: somebody who takes gifts — an actor declaring `giveInteractions`; the affordance HELP reads. */
const requiresAGiftTaker = staticRule('at least one actor declaring `giveInteractions`', (contentPack) =>
	contentPack.actors.some((actor) => actor.giveInteractions.length > 0),
);

/**
 * D152: the spent refusal needs a durable thing AND a supported verb for an
 * act one of them refuses — judged together, because the verb that matters
 * is the one the pack's own declarations point at. Both tiers in one rule,
 * abstaining without a lexicon like every lexicon-tier rule.
 */
const requiresARefusedActVerb: ProducibilityRule = {
	holds: (contentPack, lexicon) => {
		const refusedActs = contentPack.items.flatMap((item) => (item.durability === undefined ? [] : refusedActsOf(item.durability)));

		if (refusedActs.length === 0) {
			return false;
		}

		return lexicon === undefined
			? undefined
			: refusedActs.some((act) => hasSupportedVerb(lexicon, INTENTS_PRODUCING_ACTION[act]));
	},
	requirement: 'at least one item declaring `durability`, and a supported verb in the merged vocabulary for an act it refuses once spent',
	tier: 'lexicon',
};

/**
 * D196: an authored act needs an item declaring `verbInteractions` AND a
 * supported verb, outside the engine's own roster, that one of those tables
 * is keyed on — judged together, like the spent refusal, because the verb
 * that matters is the one the pack's own declarations point at.
 */
const requiresAnAuthoredVerb: ProducibilityRule = {
	holds: (contentPack, lexicon) => {
		// D251: a body's table as well as a thing's.
		const authoredVerbs = [...contentPack.items, ...contentPack.actors].flatMap((owner) => Object.keys(owner.verbInteractions ?? {}));

		if (authoredVerbs.length === 0) {
			return false;
		}

		return lexicon === undefined
			? undefined
			: authoredVerbs.some((verb) => hasSupportedVerb(lexicon, [verb]) && !ENGINE_CLAIMED_INTENT_TOKENS.includes(verb));
	},
	requirement: 'at least one item or actor declaring `verbInteractions`, keyed on a supported verb of the pack\'s own in the merged vocabulary',
	tier: 'lexicon',
};

/**
 * `command_rejected:<reason>` — what each refusal needs before this pack can
 * ever utter it. A reason absent from this record is always producible; only
 * reasons with structural or vocabulary preconditions appear. Emit-site
 * provenance for every rule lives in the R15 handoff.
 */
export const REJECTION_REASON_RULES: Readonly<Partial<Record<string, readonly ProducibilityRule[]>>> = {
	// D232: a body heard and not reached needs an actor that is heard from somewhere else.
	actor_out_of_reach: [
		staticRule('at least one actor declaring `audibleFrom`', (contentPack) => contentPack.actors.some((actor) => actor.audibleFrom.length > 0)),
	],
	// D143: the wear family's refusals — a wearable and a wear verb; the slot
	// refusal additionally needs two things that want one slot.
	already_equipped: [requiresAWearable, requiresAWearVerb],
	already_held: [
		requiresATakeable,
		lexiconRule('a supported `take` or `remove` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['remove', 'take'])),
	],
	// D135: a `close` refused in the item's own words needs an item that says them.
	close_refused: [
		staticRule('at least one item declaring `closeRefusal`', (contentPack) => contentPack.items.some((item) => item.closeRefusal !== undefined)),
		lexiconRule('a supported `close` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['close'])),
	],
	condition_unmet: [
		staticRule('at least one use interaction declaring conditions', (contentPack) =>
			allUseInteractions(contentPack).some((interaction) => interaction.conditions.length > 0),
		),
		lexiconRule('a supported `use` or `open` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['open', 'use'])),
	],
	// D135: a door already where it was asked to be — needs a door and a door verb.
	door_unmoved: [
		requiresAnOpenable,
		lexiconRule('a supported `open` or `close` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['close', 'open'])),
	],
	exit_leads_outward: [
		staticRule('at least one exit declaring `leadsOutOfPack`', (contentPack) =>
			allExits(contentPack).some((exit) => exit.leadsOutOfPack === true),
		),
	],
	exit_locked: [
		// The founding case: the outward check runs BEFORE the lock check
		// (D19a), so a lock on an outward exit can never speak this reason.
		staticRule('at least one non-outward exit carrying a lock', (contentPack) =>
			allExits(contentPack).some((exit) => exit.lock !== undefined && exit.leadsOutOfPack !== true),
		),
	],
	// D234: a fallen foe struck again — a foe, and the verb that strikes it.
	foe_defeated: [requiresAFoe, requiresAnAttackVerb],
	// D235: a gift arm whose conditions do not hold — an arm WITH conditions, and the verb.
	gift_condition_unmet: [
		staticRule('at least one `giveInteractions` arm declaring conditions', (contentPack) =>
			contentPack.actors.some((actor) => actor.giveInteractions.some((interaction) => interaction.conditions.length > 0)),
		),
		requiresAGiftVerb,
	],
	// D235: a thing offered that no arm takes — a body to offer it to, and the verb.
	gift_refused: [staticRule('at least one actor', (contentPack) => contentPack.actors.length > 0), requiresAGiftVerb],
	hands_are_not_enough: [
		// Interceptor-driven (D63): presence is the bar, not supportedness.
		lexiconRule('an `open` or `use` verb in the merged vocabulary', (lexicon) => hasVerb(lexicon, ['open', 'use'])),
	],
	item_spent: [requiresARefusedActVerb],
	missing_direction: [lexiconRule('a typed movement verb in the merged vocabulary (bare `go`)', hasTypedMovementVerb)],
	missing_instrument: [
		staticRule('at least one authored use interaction (an instrument to be missing)', (contentPack) =>
			allUseInteractions(contentPack).length > 0,
		),
		lexiconRule('a supported `open` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['open'])),
	],
	// D235: "to whom?" is asked only where somebody takes gifts — else D47's refusal speaks.
	missing_recipient: [requiresAGiftTaker, requiresAGiftVerb],
	missing_target: [lexiconRule('a supported `use` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['use']))],
	no_interaction_possible: [
		lexiconRule('a supported `open` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['open'])),
	],
	no_matching_interaction: [
		lexiconRule('a supported `use` or `open` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['open', 'use'])),
	],
	no_path_toward: [
		lexiconRule('a typed movement verb in the merged vocabulary (`go to <thing>`)', hasTypedMovementVerb),
	],
	// D234: `attack` aimed at a body with no `combat` block — a body that is not a foe, and the verb.
	not_a_foe: [
		staticRule('at least one actor declaring no `combat`', (contentPack) => contentPack.actors.some((actor) => actor.combat === undefined)),
		requiresAnAttackVerb,
	],
	// D234: `attack … with <thing>` where the thing is no weapon — a foe, a thing declaring no `weapon`, and the verb.
	not_a_weapon: [
		requiresAFoe,
		staticRule('at least one item declaring no `weapon`', (contentPack) => contentPack.items.some((item) => item.weapon === undefined)),
		requiresAnAttackVerb,
	],
	not_equippable: [requiresAWearVerb],
	// D234: a wearable thing that is not worn — asked to come off, or (`attack … with` it) swung while merely carried.
	not_equipped: [
		requiresAWearable,
		lexiconRule('a supported `equip`, `unequip`, `remove` or `attack` verb in the merged vocabulary', (lexicon) =>
			hasSupportedVerb(lexicon, ['attack', 'equip', 'remove', 'unequip']),
		),
	],
	not_openable: [lexiconRule('a supported `close` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['close']))],
	not_portable: [
		staticRule('at least one non-portable or ambient item', (contentPack) =>
			contentPack.items.some((item) => !isTakeable(contentPack, item)),
		),
		lexiconRule('a supported `take` or `remove` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['remove', 'take'])),
	],
	// D135: an `open` refused in the item's own words needs an item that says them.
	open_refused: [
		staticRule('at least one item declaring `openRefusal`', (contentPack) => contentPack.items.some((item) => item.openRefusal !== undefined)),
		lexiconRule('a supported `open` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['open'])),
	],
	// D252: an order no arm answers — anybody can be told something, so a body is all it takes.
	order_unanswered: [staticRule('at least one actor', (contentPack) => contentPack.actors.length > 0)],
	// D217: a set answered in one line refuses every verb but looking — a pack that answers each member never says it.
	plural_one_at_a_time: [
		staticRule('`plurals` answering a set in one line (`set`, or `each_up_to` beyond its count)', (contentPack) => contentPack.plurals.answer !== 'each'),
	],
	search_while_shut: [
		requiresAnOpenable,
		lexiconRule('a supported `search` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['search'])),
	],
	slot_occupied: [
		staticRule('two items declaring the same equippable slot', (contentPack) => {
			const slots = contentPack.items.flatMap((item) => (item.equippable === undefined ? [] : [item.equippable.slot]));

			return new Set(slots).size < slots.length;
		}),
		requiresAWearVerb,
	],
	// D244: a stock refusing — empty, or with nowhere to put what it gives — needs a stock, and the verb that draws from it.
	stock_empty: [requiresAStock, requiresATakeVerb],
	stock_full: [requiresAStock, requiresATakeVerb],
	switch_latched: [
		staticRule('at least one switchable item declaring `latching`', (contentPack) =>
			contentPack.items.some((item) => item.switchable?.latching === true),
		),
		// Interceptor-driven (D56): `turn` is declared unsupported on purpose.
		lexiconRule('a `turn` verb in the merged vocabulary', (lexicon) => hasVerb(lexicon, ['turn'])),
	],
	// D260: a switch held in its own words needs a switch that declares the refusal.
	switch_refused: [
		staticRule('at least one switchable item declaring `turnOnRefusal` or `turnOffRefusal`', (contentPack) =>
			contentPack.items.some((item) => item.switchable?.turnOnRefusal !== undefined || item.switchable?.turnOffRefusal !== undefined),
		),
		// Interceptor-driven (D56): `turn` is declared unsupported on purpose.
		lexiconRule('a `turn` verb in the merged vocabulary', (lexicon) => hasVerb(lexicon, ['turn'])),
	],
	switch_unmoved: [
		staticRule('at least one switchable item', (contentPack) => contentPack.items.some((item) => item.switchable !== undefined)),
		// Interceptor-driven (D56): `turn` is declared unsupported on purpose.
		lexiconRule('a `turn` verb in the merged vocabulary', (lexicon) => hasVerb(lexicon, ['turn'])),
	],
	// D199: a `take` refused in the item's own words needs an item that says them.
	take_refused: [
		staticRule('at least one item declaring `takeRefusal`', (contentPack) => contentPack.items.some((item) => item.takeRefusal !== undefined)),
		lexiconRule('a supported `take` or `remove` verb in the merged vocabulary', (lexicon) => hasSupportedVerb(lexicon, ['remove', 'take'])),
	],
	unknown_intent: [
		lexiconRule('a supported verb the narrowing roster does not answer', (lexicon) =>
			[...lexicon.intentsByToken.values()].some(
				(intent) => !intent.isUnsupported && !NARROWABLE_INTENT_TOKENS.includes(intent.canonicalToken),
			),
		),
	],
	unsupported_intent: [
		lexiconRule('a declared-unsupported verb, or any supported verb outside `inspect`/`look`/`search` (the room-aimed path, D41)', (lexicon) =>
			[...lexicon.intentsByToken.values()].some(
				(intent) => intent.isUnsupported || !['inspect', 'look', 'search'].includes(intent.canonicalToken),
			),
		),
	],
	// D232: an item verb aimed at a body — any verb but the ones an actor
	// answers, aimed at any actor.
	verb_on_actor: [
		staticRule('at least one actor', (contentPack) => contentPack.actors.length > 0),
		lexiconRule('a verb other than `ask`, `inspect` and `look` in the merged vocabulary', (lexicon) =>
			[...lexicon.intentsByToken.values()].some((intent) => !['ask', 'inspect', 'look'].includes(intent.canonicalToken)),
		),
	],
	// D196: any supported verb of the pack's own, aimed at a thing with no arm
	// for it — the SAME precondition as `unknown_intent`, because the one verb
	// reaches both refusals depending on whether the player named an object.
	verb_unanswered: [
		lexiconRule('a supported verb the narrowing roster does not answer', (lexicon) =>
			[...lexicon.intentsByToken.values()].some(
				(intent) => !intent.isUnsupported && !NARROWABLE_INTENT_TOKENS.includes(intent.canonicalToken),
			),
		),
	],
};

/**
 * `command_executed:<action>` — what each action needs before a command can
 * ever COMPLETE as it (a narrowed action its handler refuses has not
 * executed). Verb requirements come from {@link INTENTS_PRODUCING_ACTION};
 * an action with an empty verb list (movement's direction shorthands) has no
 * vocabulary requirement.
 */
const EXECUTED_ACTION_STATIC_RULES: Readonly<Partial<Record<string, ProducibilityRule>>> = {
	// D196: no fixed verb (the table's list is empty, like `go`), so the whole
	// requirement lives here — a table on an item, keyed on a verb of the pack's own.
	act: requiresAnAuthoredVerb,
	// D234: a strike needs a foe (the verb requirement is the table's).
	attack: requiresAFoe,
	close_container: requiresAnOpenable,
	equip: requiresAWearable,
	// D235: a thing handed or shown needs somebody with an arm to take it (the verb requirement is the table's).
	give: requiresAGiftTaker,
	go: staticRule('at least one exit with a target room (an internal traversal)', (contentPack) =>
		allExits(contentPack).some((exit) => exit.targetRoomId !== undefined),
	),
	// D232: looking at a body needs a body.
	inspect_actor: staticRule('at least one actor', (contentPack) => contentPack.actors.length > 0),
	open_container: requiresAnOpenable,
	// D252: an order a body answers needs a body with an `orders` table.
	order: requiresAnOrderTaker,
	show: requiresAGiftTaker,
	switch_off: staticRule('at least one item declaring `switchable`', (contentPack) =>
		contentPack.items.some((item) => item.switchable !== undefined),
	),
	switch_on: staticRule('at least one item declaring `switchable`', (contentPack) =>
		contentPack.items.some((item) => item.switchable !== undefined),
	),
	take: requiresATakeable,
	unequip: requiresAWearable,
	use: staticRule('at least one authored use interaction', (contentPack) => allUseInteractions(contentPack).length > 0),
};

/** The rules for one `command_executed` action type. */
export const executedActionRules = (actionType: string): readonly ProducibilityRule[] => {
	/*
	 * UNREACHABLE `?? []`, and the type system is the reason it has to be written.
	 *
	 * `executedActionRules` has exactly one caller, and that caller is guarded by
	 * `RUNTIME_ACTION_TYPES.includes(detail)`. Both rosters are exhaustive over
	 * the SAME union by compiler: `RUNTIME_ACTION_TYPES` derives from a
	 * `Record<RuntimeAction['type'], true>` and `INTENTS_PRODUCING_ACTION` is a
	 * `Record<RuntimeAction['type'], readonly string[]>`, both `as const
	 * satisfies`, so excess-property checking makes their key sets EQUAL rather
	 * than merely overlapping. There is no action type an author can name that
	 * passes the guard and misses the lookup.
	 *
	 * The `?? []` exists because the widening cast to `Record<string, …>` — which
	 * the lookup needs in order to accept a runtime string at all — throws away
	 * the very exhaustiveness that makes it safe. `noUncheckedIndexedAccess`
	 * (tooling/typescript/tsconfig.base.json) then requires the default.
	 *
	 * Verified 2026-08-23: a throwing default leaves the whole repo suite green.
	 * The empty-list case (`go: []`, an action no verb narrows to) is a DIFFERENT
	 * branch and is covered.
	 */
	/* v8 ignore next */
	const producingIntents = (INTENTS_PRODUCING_ACTION as Readonly<Record<string, readonly string[]>>)[actionType] ?? [];
	const staticRuleForAction = EXECUTED_ACTION_STATIC_RULES[actionType];
	// The switch actions are interceptor-driven (D56) and reachable through a
	// declared-unsupported `turn`; every other roster verb must be supported.
	const verbRule =
		producingIntents.length === 0
			? []
			: [
				actionType === 'switch_off' || actionType === 'switch_on'
					? lexiconRule(`a \`${producingIntents.join('` or `')}\` verb in the merged vocabulary`, (lexicon) => hasVerb(lexicon, producingIntents))
					: lexiconRule(`a supported \`${producingIntents.join('` or `')}\` verb in the merged vocabulary`, (lexicon) =>
						hasSupportedVerb(lexicon, producingIntents),
					),
			];

	return [...(staticRuleForAction === undefined ? [] : [staticRuleForAction]), ...verbRule];
};
